import env from '@core/config/env'

export const LOCAL_API_STORAGE_KEY = 'ess.devApiRouting.v1'

export const API_SERVICES = {
  auth: {
    label: 'Authentication',
    gatewayPrefix: '/authentications',
    defaultBaseUrl: 'http://localhost:8000'
  },
  leave: {
    label: 'Leave',
    gatewayPrefix: '/leave',
    defaultBaseUrl: 'http://localhost:8001'
  },
  cert: {
    label: 'Certificate',
    gatewayPrefix: '/e-certificate',
    defaultBaseUrl: 'http://localhost:8002'
  },
  general: {
    label: 'General',
    gatewayPrefix: '/e-general',
    defaultBaseUrl: 'http://localhost:8003'
  },
  pay: {
    label: 'Payslip',
    gatewayPrefix: '/e-payslip',
    defaultBaseUrl: 'http://localhost:8004'
  },
  time: {
    label: 'Time',
    gatewayPrefix: '/e-time',
    defaultBaseUrl: 'http://localhost:8005'
  }
}

const ENDPOINT_MODES = ['local', 'remote']
const LOOPBACK_HOSTS = ['localhost', '127.0.0.1', '[::1]', '::1']

const isDevelopmentMode = () => process.env.NODE_ENV === 'development'

const trimTrailingSlash = (value = '') => value.replace(/\/+$/, '')

const normalizePath = (value = '') => {
  if (!value) return '/'
  return value.startsWith('/') ? value : `/${value}`
}

export const joinApiPath = (url = '', postUrl = '') => {
  const normalizedUrl = normalizePath(url)

  if (!postUrl) return normalizedUrl
  const normalizedPostUrl = String(postUrl)

  if (normalizedPostUrl.startsWith('?') || normalizedPostUrl.startsWith('#')) {
    return `${normalizedUrl}${normalizedPostUrl}`
  }

  return `${trimTrailingSlash(normalizedUrl)}/${normalizedPostUrl.replace(
    /^\/+/,
    ''
  )}`
}

export const getApiService = (url = '') => {
  const normalizedUrl = normalizePath(url)

  return Object.keys(API_SERVICES).find((service) => {
    const { gatewayPrefix } = API_SERVICES[service]
    return (
      normalizedUrl === gatewayPrefix ||
      normalizedUrl.startsWith(`${gatewayPrefix}/`)
    )
  })
}

export const isValidLocalBaseUrl = (baseUrl = '') => {
  try {
    const parsedUrl = new URL(baseUrl)
    return (
      ['http:', 'https:'].includes(parsedUrl.protocol) &&
      LOOPBACK_HOSTS.includes(parsedUrl.hostname)
    )
  } catch (error) {
    return false
  }
}

export const createDefaultApiRoutingConfig = () => ({
  services: Object.keys(API_SERVICES).reduce((config, service) => {
    config[service] = {
      mode: 'remote',
      baseUrl: API_SERVICES[service].defaultBaseUrl
    }
    return config
  }, {}),
  endpoints: {}
})

export const sanitizeApiRoutingConfig = (config = {}) => {
  const defaults = createDefaultApiRoutingConfig()
  const services = Object.keys(API_SERVICES).reduce((nextServices, service) => {
    const currentService = config?.services?.[service] || {}
    const baseUrl = isValidLocalBaseUrl(currentService.baseUrl)
      ? trimTrailingSlash(currentService.baseUrl)
      : defaults.services[service].baseUrl

    nextServices[service] = {
      mode: currentService.mode === 'local' ? 'local' : 'remote',
      baseUrl
    }
    return nextServices
  }, {})

  const endpoints = Object.entries(config?.endpoints || {}).reduce(
    (nextEndpoints, [apiId, mode]) => {
      if (ENDPOINT_MODES.includes(mode)) {
        nextEndpoints[apiId] = mode
      }
      return nextEndpoints
    },
    {}
  )

  return { services, endpoints }
}

const getBrowserStorage = () => {
  try {
    if (typeof window === 'undefined') return null
    return window.localStorage
  } catch (error) {
    return null
  }
}

export const loadDevApiRoutingConfig = ({
  storage = getBrowserStorage(),
  isDevelopment = isDevelopmentMode()
} = {}) => {
  const defaults = createDefaultApiRoutingConfig()
  if (!isDevelopment || !storage) return defaults

  try {
    const savedConfig = storage.getItem(LOCAL_API_STORAGE_KEY)
    return savedConfig
      ? sanitizeApiRoutingConfig(JSON.parse(savedConfig))
      : defaults
  } catch (error) {
    return defaults
  }
}

export const saveDevApiRoutingConfig = (
  config,
  { storage = getBrowserStorage(), isDevelopment = isDevelopmentMode() } = {}
) => {
  const nextConfig = sanitizeApiRoutingConfig(config)
  if (isDevelopment && storage) {
    storage.setItem(LOCAL_API_STORAGE_KEY, JSON.stringify(nextConfig))
  }
  return nextConfig
}

export const decorateApiRegistry = (registry) =>
  Object.entries(registry).reduce((nextRegistry, [namespace, endpoints]) => {
    if (typeof endpoints?.url === 'string') {
      nextRegistry[namespace] = {
        ...endpoints,
        apiId: namespace,
        service: getApiService(endpoints.url)
      }
      return nextRegistry
    }

    nextRegistry[namespace] = Object.entries(endpoints).reduce(
      (nextEndpoints, [name, endpoint]) => {
        nextEndpoints[name] = {
          ...endpoint,
          apiId: `${namespace}.${name}`,
          service: getApiService(endpoint.url)
        }
        return nextEndpoints
      },
      {}
    )
    return nextRegistry
  }, {})

const getLocalPath = (url, service) => {
  const normalizedUrl = normalizePath(url)
  const { gatewayPrefix } = API_SERVICES[service]
  const localPath = normalizedUrl.slice(gatewayPrefix.length)
  return normalizePath(localPath)
}

export const resolveApiRequest = ({
  apiId,
  service,
  url,
  postUrl,
  config,
  remoteBaseURL = env.API_URL,
  isDevelopment = isDevelopmentMode()
}) => {
  const requestUrl = joinApiPath(url, postUrl)
  const resolvedService = service || getApiService(requestUrl)

  if (!isDevelopment || !resolvedService || !API_SERVICES[resolvedService]) {
    return {
      baseURL: remoteBaseURL,
      url: requestUrl,
      mode: 'remote',
      service: resolvedService
    }
  }

  const routingConfig = sanitizeApiRoutingConfig(
    config || loadDevApiRoutingConfig()
  )
  const endpointMode = routingConfig.endpoints[apiId]
  const serviceConfig = routingConfig.services[resolvedService]
  const mode = endpointMode || serviceConfig.mode

  if (mode !== 'local' || !isValidLocalBaseUrl(serviceConfig.baseUrl)) {
    return {
      baseURL: remoteBaseURL,
      url: requestUrl,
      mode: 'remote',
      service: resolvedService
    }
  }

  return {
    baseURL: trimTrailingSlash(serviceConfig.baseUrl),
    url: getLocalPath(requestUrl, resolvedService),
    mode: 'local',
    service: resolvedService
  }
}
